import { AiConsentGate } from './ui/AiConsentGate';
import type { ReactNode } from 'react';
import { lastSignedInUser } from './session/lastUser';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { LEGACY_PATHS, canSee, homeViewFor, pathForView, viewForPath } from '@aithora/shared';
import { SessionProvider, useConsoleRole, useSession } from './session/SessionContext';
import { Shell } from './shell/Shell';
import { SignIn } from './pages/SignIn';
import { InviteAccept } from './pages/InviteAccept';
import { RequestAccess } from './pages/RequestAccess';
import { DeleteAccount } from './pages/DeleteAccount';
import { Overview } from './pages/Overview';
import { Policy } from './pages/Policy';
import { Oversight } from './pages/Oversight';
import { Pulse } from './pages/Pulse';
import { Residents } from './pages/residents/Residents';
import { FamilyPortal } from './pages/family/FamilyPortal';
import { GuardianPage } from './pages/guardian/GuardianPage';
import { WorklistPage } from './pages/worklist/WorklistPage';
import { Capture } from './pages/Capture';
import { Finance } from './pages/Finance';
import { Performance } from './pages/Performance';
import { Atlas } from './pages/atlas/AtlasPage';
import { AtlasIntelligence } from './pages/intelligence/IntelligencePage';
import { Sales } from './pages/Sales';
import { Settings } from './pages/Settings';
import { NoAccess, Placeholder } from './pages/Placeholder';
import { Companion } from './pages/companion/Companion';
import { AithoraHome } from './pages/home/AithoraHome';
import { HelpPage } from './pages/help/HelpPage';
import { GuidePage } from './pages/guide/GuidePage';
import { LangProvider } from './i18n/LangContext';

/** Signed-out visitors go to sign-in, remembering only the page they asked for. */
function RequireSession({ children }: { children: ReactNode }) {
  const { session, checking } = useSession();
  const location = useLocation();
  if (checking) {
    return (
      <div className="boot" aria-busy="true">
        Checking your session{'…'}
      </div>
    );
  }
  // The page is remembered with whose it was, so only that person is sent back to it.
  if (!session) return <Navigate to="/signin" replace state={{ from: location.pathname, fromUser: lastSignedInUser() }} />;
  return <>{children}</>;
}

/** The roles V6 sends from Atlas to Atlas > Conversations (design STAFF_CONV). */
const STAFF_CONV = new Set(['SUPER_ADMIN', 'OPERATOR', 'ADMIN', 'CAREGIVER', 'STAFF', 'PARTNER']);

function Landing() {
  const { role } = useConsoleRole();
  const home = homeViewFor(role);
  return home ? <Navigate to={pathForView(home)} replace /> : <NoAccess />;
}

/**
 * One route for every page. The path picks the page; whether the role may open it
 * comes from the session only.
 */
function Page() {
  const { role } = useConsoleRole();
  const { page = '' } = useParams();
  const loc = useLocation();
  const view = viewForPath(`/${page}`);
  if (!view) return <NoSuchPage />;
  // An old address (/companion) opens the page at its current one (/me), keeping the rest.
  const legacy = LEGACY_PATHS[`/${page}`];
  if (legacy && !(view === 'companion' && role && STAFF_CONV.has(role))) {
    const rest = loc.pathname.replace(/^\/[^/]+/, '');
    return <Navigate to={legacy + rest + loc.search + loc.hash} replace />;
  }
  // Atlas is also opened by Aithora Home members (from Home) and Family (from the
  // Family Portal) without a side-nav entry; the server scopes what each one gets.
  if (view === 'companion' && (role === 'MEMBER' || role === 'FAMILY_MEMBER')) return <Companion />;
  // The Facility Admin answers families from the Family Portal's care-team view: the
  // Overview's "Families waiting" rows open it. No side-nav entry (the design has none
  // for this role); before, those rows led to "no access" and nobody could reply.
  if (view === 'family' && role === 'ADMIN') return <FamilyPortal />;
  // Console V6 (convRedirect): staff who open the old Atlas address read residents'
  // conversations in Atlas > Conversations now.
  if (view === 'companion' && role && STAFF_CONV.has(role)) return <Navigate to="/atlas?tab=conversations" replace />;
  if (!canSee(role, view)) return <NoAccess />;
  if (view === 'overview') return <Overview />;
  if (view === 'policy') return <Policy />;
  if (view === 'oversight') return <Oversight />;
  if (view === 'pulse') return <Pulse />;
  if (view === 'residents') return <Residents />;
  if (view === 'family') return <FamilyPortal />;
  if (view === 'guardian') return <GuardianPage />;
  if (view === 'worklist') return <WorklistPage />;
  if (view === 'companion') return <Companion />;
  if (view === 'home') return <AithoraHome />;
  if (view === 'help') return <HelpPage />;
  if (view === 'capture') return <Capture />;
  if (view === 'finance') return <Finance />;
  if (view === 'performance') return <Performance />;
  // Family see the AI consent notice before their first Atlas use, as on the phone (same record).
  if (view === 'atlas' && role === 'FAMILY_MEMBER') return <AiConsentGate><Atlas /></AiConsentGate>;
  // Kept in this exact form: scripts/gen-qa-map.ts maps each view to its page by `if (view === 'x') return <X />`.
  if (view === 'atlas') return <Atlas />;
  if (view === 'atlasIntel') return <AtlasIntelligence />;
  if (view === 'sales') return <Sales />;
  if (view === 'settings') return <Settings />;
  return <Placeholder view={view} />;
}

function NoSuchPage() {
  return (
    <section className="placeholder">
      <h2 className="placeholder__title">No page here</h2>
      <p className="placeholder__body">That address does not match a console page.</p>
    </section>
  );
}

export function AppRoutes() {
  return (
    <LangProvider>
    <Routes>
      <Route path="/signin" element={<SignIn />} />
      {/* Signed out: the invite link's page and the public access request. */}
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/request-access" element={<RequestAccess />} />
      {/* Delete my account: the stores' public address; signed in or out (pages/DeleteAccount). */}
      <Route path="/delete-account" element={<DeleteAccount />} />
      {/* The Guide: its own chrome, outside the shell; the role comes from the session. */}
      <Route path="/guide/*" element={<RequireSession><GuidePage /></RequireSession>} />
      <Route
        element={
          <RequireSession>
            <Shell />
          </RequireSession>
        }
      >
        <Route index element={<Landing />} />
        <Route path="/no-access" element={<NoAccess />} />
        <Route path="/:page/*" element={<Page />} />
      </Route>
    </Routes>
    </LangProvider>
  );
}

/**
 * Invite links minted for the Flutter console read /#/invite/<token>; the same link opens
 * the invite page here. Runs once, before the router reads the address.
 */
export function redirectHashRoute(): void {
  if (typeof window === 'undefined') return;
  const m = /^#\/(invite\/[^/?#]+|request-access|delete-account)$/.exec(window.location.hash);
  if (m) window.history.replaceState(null, '', '/' + m[1]);
}

export function App() {
  redirectHashRoute();
  return (
    <SessionProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </SessionProvider>
  );
}
